import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, Field, Modal, Textarea } from '@/components/ds';
import { useFormatters } from '@/hooks/useFormatters';
import {
  registerVisitConfirmAsker,
  registerVisitReasonAsker,
  type VisitReasonCode,
  type VisitRefusal,
} from '@/lib/visitConfirm';

/**
 * Asks staff to confirm a visit action the API refused: checking in a visit
 * booked for a later day, closing a visit with unfinished procedures, or
 * booking a patient who already has an appointment at that time.
 * Mounted once in the shell; the API client opens it.
 */
export function VisitConfirmHost() {
  const { t } = useTranslation();
  const { date, dateTime } = useFormatters();
  const [refusal, setRefusal] = useState<VisitRefusal | null>(null);
  const resolver = useRef<((confirmed: boolean) => void) | null>(null);

  useEffect(
    () =>
      registerVisitConfirmAsker(
        (next) =>
          new Promise<boolean>((resolve) => {
            // A second refusal while one is still open is answered "no".
            resolver.current?.(false);
            resolver.current = resolve;
            setRefusal(next);
          }),
      ),
    [],
  );

  // A refusal answered with a reason (a finished visit moved to another
  // doctor): the reason is kept with the change.
  const [reasonCode, setReasonCode] = useState<VisitReasonCode | null>(null);
  const [reasonText, setReasonText] = useState('');
  const reasonResolver = useRef<((reason: string | null) => void) | null>(null);
  useEffect(
    () =>
      registerVisitReasonAsker(
        (code) =>
          new Promise<string | null>((resolve) => {
            reasonResolver.current?.(null);
            reasonResolver.current = resolve;
            setReasonText('');
            setReasonCode(code);
          }),
      ),
    [],
  );
  const answerReason = (reason: string | null) => {
    reasonResolver.current?.(reason);
    reasonResolver.current = null;
    setReasonCode(null);
  };

  const answer = (confirmed: boolean) => {
    resolver.current?.(confirmed);
    resolver.current = null;
    setRefusal(null);
  };

  const names = (refusal?.procedures ?? [])
    .map((p) => p.name)
    .filter(Boolean)
    .join(', ');

  const hoursKey =
    refusal?.reason === 'BRANCH_CLOSED'
      ? 'closed'
      : refusal?.reason === 'DOCTOR_NOT_WORKING'
        ? 'doctorOff'
        : 'outside';

  const copy =
    refusal?.code === 'OUTSIDE_WORKING_HOURS'
      ? {
          title: t(`reservations.visitConfirm.hours.${hoursKey}.title`),
          message: refusal.closureReason
            ? t('reservations.visitConfirm.hours.closedWithReason', {
                reason: refusal.closureReason,
              })
            : t(`reservations.visitConfirm.hours.${hoursKey}.message`),
          confirm: t('reservations.visitConfirm.hours.confirm'),
        }
      : refusal?.code === 'PATIENT_DOUBLE_BOOKED'
        ? {
            title: t('reservations.visitConfirm.patientOverlap.title'),
            message: refusal.branchName
              ? t('reservations.visitConfirm.patientOverlap.messageBranch', {
                  time: dateTime(refusal.scheduledAt),
                  branch: refusal.branchName,
                })
              : t('reservations.visitConfirm.patientOverlap.message', {
                  time: dateTime(refusal.scheduledAt),
                }),
            confirm: t('reservations.visitConfirm.patientOverlap.confirm'),
          }
        : refusal?.code === 'VISIT_NOT_TODAY'
          ? {
              title: t('reservations.visitConfirm.notToday.title'),
              message: t('reservations.visitConfirm.notToday.message', {
                date: refusal.scheduledAt ? date(refusal.scheduledAt) : '',
              }),
              confirm: t('reservations.visitConfirm.notToday.confirm'),
            }
          : {
              title: t('reservations.visitConfirm.pending.title', {
                count: refusal?.count ?? refusal?.procedures?.length ?? 0,
              }),
              message: names
                ? t('reservations.visitConfirm.pending.messageNamed', { names })
                : t('reservations.visitConfirm.pending.message'),
              confirm: t('reservations.visitConfirm.pending.confirm'),
              // Closing drops the unfinished work from the bill, so it is the
              // destructive choice; going back to finish them is the default.
              cancel: t('reservations.visitConfirm.pending.cancel'),
              destructive: true,
            };

  return (
    <>
      <Modal
        isOpen={reasonCode !== null}
        onClose={() => answerReason(null)}
        size="md"
        title={t('reservations.visitConfirm.doctorChange.title')}
        confirmLabel={t('reservations.visitConfirm.doctorChange.confirm')}
        onConfirm={() => answerReason(reasonText.trim() || null)}
        isConfirmDisabled={!reasonText.trim()}
        cancelLabel={t('common.cancel')}
      >
        <Field label={t('reservations.visitConfirm.doctorChange.reasonLabel')}>
          <Textarea
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={t(
              'reservations.visitConfirm.doctorChange.reasonPlaceholder',
            )}
          />
        </Field>
      </Modal>
      <ConfirmModal
        isOpen={refusal !== null}
        onClose={() => answer(false)}
        onConfirm={() => answer(true)}
        title={copy.title}
        message={copy.message}
        confirmLabel={copy.confirm}
        cancelLabel={'cancel' in copy ? copy.cancel : undefined}
        isDestructive={'destructive' in copy && copy.destructive}
      />
    </>
  );
}
